/**
 * Filter Algolia facet results to those allowed by a PLP configuration.
 *
 * Shared by ProductListingPage and the search orchestrator so facet visibility
 * stays aligned with PLP_CONFIGS.
 */

import type { FacetResults } from '@/lib/algolia/types';
import { devLog } from '@/lib/utils/devLog';

import { PLP_CONFIGS, resolveFacetAttribute, type PLPConfig, type PLPType } from './plpConfig';

export interface FilterFacetsOptions {
  /** Omit the price facet (search never shows price filters). */
  excludePrice?: boolean;
}

/**
 * Keep only facet keys configured for the current PLP type.
 */
export function filterFacetsByConfig(
  facets: FacetResults | undefined,
  config: PLPConfig,
  localeCode: string,
  options?: FilterFacetsOptions
): FacetResults | undefined {
  if (!facets) return undefined;

  const allowedFacets = new Set(
    config.facets.map((f) => resolveFacetAttribute(f.attribute, localeCode))
  );

  devLog(
    '[filterFacetsByConfig] Config facets:',
    config.facets.map((f) => f.attribute)
  );
  devLog('[filterFacetsByConfig] Allowed facets after resolution:', Array.from(allowedFacets));
  devLog('[filterFacetsByConfig] Incoming facet keys:', Object.keys(facets));

  const filtered: FacetResults = {};
  for (const [facetName, values] of Object.entries(facets)) {
    if (options?.excludePrice && facetName === 'price') continue;
    if (allowedFacets.has(facetName)) {
      filtered[facetName] = values;
    }
  }

  devLog('[filterFacetsByConfig] Filtered facet keys:', Object.keys(filtered));

  return filtered;
}

/**
 * Convenience wrapper that resolves PLP config by type key.
 */
export function filterFacetsByPlpType(
  facets: FacetResults | undefined,
  plpType: PLPType,
  localeCode: string,
  options?: FilterFacetsOptions
): FacetResults | undefined {
  return filterFacetsByConfig(facets, PLP_CONFIGS[plpType], localeCode, options);
}

/** Remove price facet from filtered facet counts (search UI never shows price). */
export function stripPriceFacet(facets: FacetResults | undefined): FacetResults | undefined {
  if (!facets || !('price' in facets)) return facets;
  const { price: _price, ...rest } = facets;
  return rest;
}
